import {
  CampanaProductiva,
  GranoProductivo,
  ZonaProductiva,
} from "@/services/productive-entities.service";
import { AppColors } from "@/styles/colors";
import { Card, Flex, Grid, GridItem } from "@chakra-ui/react";
import { memo } from "react";
import { Control, FieldErrors } from "react-hook-form";
import { ZonalGrossMarginCreationForm } from "../create/schema/zonal-gross-margin-creation.schema";
import {
  CurrencyField,
  NumericFieldWithComma,
  SectionTitle,
  SelectField,
} from "./ZonalGrossMarginFormFields";

interface Props {
  control: Control<ZonalGrossMarginCreationForm>;
  errors: FieldErrors<ZonalGrossMarginCreationForm>;
  zones: ZonaProductiva[];
  grains: GranoProductivo[];
  campaigns: CampanaProductiva[];
  isActive: boolean;
  loading: boolean;
  loadingOptions: boolean;
}

export const ZonalGrossMarginForm = ({
  control,
  errors,
  zones,
  grains,
  campaigns,
  isActive,
  loading,
  loadingOptions,
}: Props) => {
  const disabled = loading || loadingOptions || !isActive;

  return (
    <Flex flexDir="column" gap="24px" marginTop="30px">
      <Card.Root maxW="full" borderRadius="8px">
        <Card.Body
          paddingX="32px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" }}
            gap={6}
          >
            <SelectField
              name="idZonaProductiva"
              label="Zona Productiva"
              items={(zones ?? []).map((item) => ({
                label: item.nombre,
                value: String(item.id),
              }))}
              control={control}
              disabled={disabled}
              loading={loadingOptions}
              errors={errors}
            />

            <SelectField
              name="idGranoProductivo"
              label="Grano"
              items={(grains ?? []).map((item) => ({
                label: item.nombre,
                value: String(item.id),
              }))}
              control={control}
              disabled={disabled}
              loading={loadingOptions}
              errors={errors}
            />

            <SelectField
              name="idCampanaProductiva"
              label="Campaña"
              items={(campaigns ?? []).map((item) => ({
                label: item.nombre,
                value: String(item.id),
              }))}
              control={control}
              disabled={disabled}
              loading={loadingOptions}
              errors={errors}
            />

            <GridItem>
              <CurrencyField
                name="cotizacionDolar"
                label="Cotización Dólar"
                control={control}
                disabled={disabled}
                errors={errors}
                prefix="$ "
              />
            </GridItem>

            <GridItem>
              <CurrencyField
                name="precioCereal"
                label="Precio Cereal Neto"
                control={control}
                disabled={disabled}
                errors={errors}
                prefix="$ "
              />
            </GridItem>

            <SectionTitle>Rinde</SectionTitle>

            <GridItem>
              <NumericFieldWithComma
                name="rindeEstimadoZonal"
                label="Rinde estimado zonal (qq/ha)"
                control={control}
                disabled={disabled}
              />
            </GridItem>

            <SectionTitle>Implantación</SectionTitle>

            <GridItem>
              <CurrencyField
                name="precioAgroquimicos"
                label="Agroquímicos"
                control={control}
                disabled={disabled}
                errors={errors}
                
              />
            </GridItem>

            <GridItem>
              <CurrencyField
                name="precioFertilizantes"
                label="Fertilizante"
                control={control}
                disabled={disabled}
                errors={errors}
              />
            </GridItem>

            <GridItem>
              <CurrencyField
                name="precioSemillas"
                label="Semillas"
                control={control}
                disabled={disabled}
                errors={errors}
              />
            </GridItem>

            <GridItem>
              <CurrencyField
                name="precioLabores"
                label="Labores"
                control={control}
                disabled={disabled}
                errors={errors}
              />
            </GridItem>

            <GridItem>
              <CurrencyField
                name="precioSeguros"
                label="Seguro"
                control={control}
                disabled={disabled}
                errors={errors}
              />
            </GridItem>

            <GridItem>
              <CurrencyField
                name="costoImplantacion"
                label="Costo de implantación"
                control={control}
                disabled
                errors={errors}
              />
            </GridItem>

            <SectionTitle>Otros Gastos</SectionTitle>

            <GridItem>
              <CurrencyField
                name="precioAlquiler"
                label="Alquiler"
                control={control}
                disabled={disabled}
                errors={errors}
                required={false}
              />
            </GridItem>

            <GridItem>
              <NumericFieldWithComma
                name="gastoDeCosecha"
                label="Gasto de cosecha (%)"
                control={control}
                disabled={disabled}
              />
            </GridItem>

            <SectionTitle>Gastos de implantación</SectionTitle>

            <GridItem>
              <CurrencyField
                name="inversionTotal"
                label="Inversión Total"
                control={control}
                disabled
                errors={errors}
              />
            </GridItem>

            <GridItem>
              <NumericFieldWithComma
                name="equilibrio"
                label="Equilibrio (en qq/ha)"
                control={control}
                disabled={disabled}
              />
            </GridItem>
          </Grid>
        </Card.Body>
      </Card.Root>
    </Flex>
  );
};

export const ZonalGrossMarginFormMemo = memo(ZonalGrossMarginForm);
